/**
 * Materials Reconciliation
 * ────────────────────────
 * Single source-of-truth join between the canonical Job BOM (resolved via
 * `getJobBom()` — JobSpec snapshot first) and the execution ledger that lives
 * on `job.materialsList`. The two are NOT the same thing:
 *
 *   • `getJobBom(job).items` → what the contract says the job needs (sold spec)
 *   • `job.materialsList`    → what the warehouse/ordering system has actually
 *                              required, allocated, staged, installed
 *
 * Surfaces (Inventory page, issue intelligence, dispatch) MUST consume the
 * reconciler instead of `materialsList` directly. This guarantees:
 *   1. Every spec-bound BOM line shows up — even if no ledger row exists yet
 *      ("unallocated" status with execution counters at 0).
 *   2. Any ledger row whose SKU is NOT on the spec is surfaced as "off-spec"
 *      so it can be reviewed (no silent drift).
 *   3. Required quantity is always taken from the canonical BOM when present;
 *      ledger-only rows fall back to ledger qty and are flagged.
 *
 * This file implements two helpers:
 *   • getJobBomReconciliation(job) → read-side projection used by UI
 *   • reconcileMaterialsListWithBom(job) → write-side helper that returns a
 *     hydrated MaterialLineItem[] safe to assign back onto a job, ensuring
 *     every spec line has a backing ledger row before the job is persisted.
 */

import type { Job, MaterialLineItem, InventoryItemStatus } from "./types";
import { getJobBom, type ResolvedJobBom } from "./jobBomSelector";

export type ReconciliationStatus =
  /** Spec line + ledger row, qty matches. */
  | "aligned"
  /** Spec line + ledger row, ledger qty differs from spec qty. */
  | "qty-drift"
  /** Spec line, no ledger row yet. */
  | "unallocated"
  /** Ledger row whose SKU is not on the canonical BOM. */
  | "off-spec"
  /** Job has no resolved BOM at all (source = "none"). */
  | "no-spec";

export interface ReconciledMaterialRow {
  sku: string;
  itemName: string;
  /** Quantity required by the canonical BOM. 0 if ledger-only (off-spec). */
  specQty: number;
  /** Quantity required by the ledger row. 0 if no ledger row exists. */
  ledgerQty: number;
  /** Effective qty to display: spec qty when present, else ledger qty. */
  displayQty: number;
  quantityAllocated: number;
  quantityStaged: number;
  quantityInstalled: number;
  missingItems: number;
  fieldPurchase: boolean;
  inventoryStatus: InventoryItemStatus;
  status: ReconciliationStatus;
  /** True only when status === "aligned" — safe to act on without review. */
  isCanonical: boolean;
}

export interface JobBomReconciliation {
  jobId: string;
  bom: ResolvedJobBom;
  rows: ReconciledMaterialRow[];
  counts: {
    aligned: number;
    qtyDrift: number;
    unallocated: number;
    offSpec: number;
    total: number;
  };
  /** True when there is at least one off-spec or qty-drift row. */
  hasDrift: boolean;
  /** True when every spec line has a matching ledger row at correct qty. */
  isFullyReconciled: boolean;
}

const ZERO_LEDGER = {
  quantityAllocated: 0,
  quantityStaged: 0,
  quantityInstalled: 0,
  missingItems: 0,
  fieldPurchase: false,
  inventoryStatus: "Allocated" as InventoryItemStatus,
};

/**
 * Read-side: compute the reconciled view of a job's materials. Pure, cheap.
 * UI MUST call this instead of touching `job.materialsList` directly.
 */
export function getJobBomReconciliation(job: Job): JobBomReconciliation {
  const bom = getJobBom(job);
  const ledger = job.materialsList ?? [];
  const ledgerBySku = new Map<string, MaterialLineItem>();
  for (const m of ledger) ledgerBySku.set(m.sku, m);

  const rows: ReconciledMaterialRow[] = [];
  const seenSpecSkus = new Set<string>();

  // 1. One row per spec line — drives the canonical view.
  for (const item of bom.items) {
    seenSpecSkus.add(item.sku);
    const led = ledgerBySku.get(item.sku);
    if (!led) {
      rows.push({
        sku: item.sku,
        itemName: item.name,
        specQty: item.quantity,
        ledgerQty: 0,
        displayQty: item.quantity,
        ...ZERO_LEDGER,
        status: bom.source === "none" ? "no-spec" : "unallocated",
        isCanonical: false,
      });
      continue;
    }
    const qtyMatches = led.quantityRequired === item.quantity;
    rows.push({
      sku: item.sku,
      itemName: led.itemName || item.name,
      specQty: item.quantity,
      ledgerQty: led.quantityRequired,
      displayQty: item.quantity,
      quantityAllocated: led.quantityAllocated,
      quantityStaged: led.quantityStaged,
      quantityInstalled: led.quantityInstalled,
      missingItems: led.missingItems,
      fieldPurchase: led.fieldPurchase,
      inventoryStatus: led.inventoryStatus,
      status: qtyMatches ? "aligned" : "qty-drift",
      isCanonical: qtyMatches && bom.isCanonical,
    });
  }

  // 2. Off-spec ledger rows — present in warehouse system but not on the BOM.
  //    Only meaningful when we actually have a canonical BOM to compare to.
  if (bom.source !== "none") {
    for (const led of ledger) {
      if (seenSpecSkus.has(led.sku)) continue;
      rows.push({
        sku: led.sku,
        itemName: led.itemName,
        specQty: 0,
        ledgerQty: led.quantityRequired,
        displayQty: led.quantityRequired,
        quantityAllocated: led.quantityAllocated,
        quantityStaged: led.quantityStaged,
        quantityInstalled: led.quantityInstalled,
        missingItems: led.missingItems,
        fieldPurchase: led.fieldPurchase,
        inventoryStatus: led.inventoryStatus,
        status: "off-spec",
        isCanonical: false,
      });
    }
  }

  let aligned = 0, qtyDrift = 0, unallocated = 0, offSpec = 0;
  for (const r of rows) {
    if (r.status === "aligned") aligned++;
    else if (r.status === "qty-drift") qtyDrift++;
    else if (r.status === "unallocated") unallocated++;
    else if (r.status === "off-spec") offSpec++;
  }

  return {
    jobId: job.id,
    bom,
    rows,
    counts: { aligned, qtyDrift, unallocated, offSpec, total: rows.length },
    hasDrift: qtyDrift > 0 || offSpec > 0,
    isFullyReconciled: rows.length > 0 && aligned === rows.length,
  };
}

/**
 * Write-side: produce a `materialsList` that is guaranteed to contain a row
 * for every spec line at the spec quantity. Existing ledger execution counters
 * are preserved. Off-spec ledger rows are kept (so warehouse can review and
 * either remove or escalate via a change order) but their `quantityRequired`
 * is left as-is; UI labels them off-spec.
 *
 * Call this whenever a job is created from a JobSpec, when a JobSpec is
 * rebound, or after a change order is applied.
 */
export function reconcileMaterialsListWithBom(job: Job): MaterialLineItem[] {
  const bom = getJobBom(job);
  if (bom.source === "none") return job.materialsList ?? [];

  const ledger = job.materialsList ?? [];
  const ledgerBySku = new Map<string, MaterialLineItem>();
  for (const m of ledger) ledgerBySku.set(m.sku, m);

  const result: MaterialLineItem[] = [];
  const seenSpecSkus = new Set<string>();

  for (const item of bom.items) {
    seenSpecSkus.add(item.sku);
    const existing = ledgerBySku.get(item.sku);
    if (existing) {
      // Force quantityRequired to the spec qty; preserve execution counters.
      result.push({ ...existing, itemName: existing.itemName || item.name, quantityRequired: item.quantity });
    } else {
      result.push({
        sku: item.sku,
        itemName: item.name,
        quantityRequired: item.quantity,
        quantityAllocated: 0,
        quantityStaged: 0,
        quantityInstalled: 0,
        missingItems: item.quantity,
        fieldPurchase: false,
        inventoryStatus: "Allocated",
        inventoryAssignment: "Assigned",
        lifecycleStage: "Assigned",
      });
    }
  }

  // Preserve off-spec ledger rows so the warehouse doesn't lose state. They
  // remain visible in the reconciler view as "off-spec" for review.
  for (const led of ledger) {
    if (!seenSpecSkus.has(led.sku)) result.push(led);
  }

  return result;
}
